import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { GlassCard } from '@/components/GlassCard';
import { Logo } from '@/components/Logo';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { ArrowLeft, Radio, ImagePlus, X, Video, LocateFixed, Search, MapPin, Loader2 } from 'lucide-react';
import { fileToDataUrl } from '@/lib/utils';
import { uploadImage } from '@/lib/mediaUpload';
import { toast } from 'sonner';
import { LiveBroadcasterDialog } from '@/components/LiveBroadcasterDialog';

export default function GoLivePage() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const { refresh } = useAuth();
  const [venue, setVenue] = useState(null); // selected venue object
  const [nearby, setNearby] = useState([]);
  const [locating, setLocating] = useState(false);
  const [vquery, setVquery] = useState('');
  const [results, setResults] = useState([]);
  const [searchBusy, setSearchBusy] = useState(false);
  const [hours, setHours] = useState(4);
  const [text, setText] = useState('');
  const [image, setImage] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [videoOn, setVideoOn] = useState(true);
  const [session, setSession] = useState(null); // { url, token, room, venue_name }
  const fileRef = React.useRef();
  const venueId = venue?.id || '';

  useEffect(() => {
    const preselect = params.get('venue');
    if (preselect) {
      api.get(`/venues/${preselect}`).then((r) => setVenue(r.data)).catch(() => {});
    }
  }, []);

  const pingLocation = () => {
    if (!navigator.geolocation) return toast.error('Location is not available on this device');
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const r = await api.get('/venues', {
            params: { lat: pos.coords.latitude, lon: pos.coords.longitude, limit: 15 },
          });
          setNearby(r.data || []);
          if (!r.data?.length) toast.info('No venues found around you — try searching by name');
        } catch {
          toast.error('Could not load venues near you');
        } finally {
          setLocating(false);
        }
      },
      () => {
        setLocating(false);
        toast.error('Location permission denied — search for your venue instead');
      },
      { enableHighAccuracy: true, timeout: 12000 }
    );
  };

  useEffect(() => {
    const q = vquery.trim();
    if (q.length < 2) {
      setResults([]);
      return undefined;
    }
    setSearchBusy(true);
    const handle = setTimeout(() => {
      api.get('/venues', { params: { q, limit: 15 } })
        .then((r) => setResults(r.data || []))
        .catch(() => setResults([]))
        .finally(() => setSearchBusy(false));
    }, 300);
    return () => clearTimeout(handle);
  }, [vquery]);

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setImage(await fileToDataUrl(f));
  };

  const submit = async () => {
    if (!venueId) return toast.error('Pick a venue');
    setSubmitting(true);
    try {
      const imageUrl = await uploadImage(image);
      if (videoOn) {
        const { data } = await api.post('/live/start', { venue_id: venueId, hours });
        if (text.trim() || image) {
          await api.post('/posts', {
            text: text.trim() || 'Live from the venue',
            image_data_url: imageUrl,
            venue_id: venueId,
          });
        }
        await refresh();
        setSession(data);
      } else {
        await api.post('/golive', { venue_id: venueId, hours });
        if (text.trim() || image) {
          await api.post('/posts', {
            text: text.trim() || 'Live from the venue',
            image_data_url: imageUrl,
            venue_id: venueId,
          });
        }
        await refresh();
        toast.success('You are live!');
        nav('/');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to go live');
    } finally {
      setSubmitting(false);
    }
  };

  const stopBroadcast = async () => {
    try {
      await api.post('/live/stop');
    } catch (e) {
      // proceed regardless — user wants out
    }
    setSession(null);
    await refresh();
    toast.success('Live stream ended');
    nav('/');
  };

  return (
    <div className="min-h-screen th-hero-radial">
      <header className="sticky top-0 z-30 bg-[#07070A]/85 backdrop-blur-xl border-b border-white/5">
        <div className="mx-auto flex max-w-2xl items-center gap-2 px-4 py-3">
          <Button variant="ghost" size="icon" onClick={() => nav(-1)} className="text-white hover:bg-white/10" data-testid="golive-back-button">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="font-display text-lg font-semibold" style={{ fontFamily: 'Space Grotesk' }}>Go Live</h1>
          <div className="ml-auto"><Logo size={96} withText={false} /></div>
        </div>
      </header>

      <main className="mx-auto max-w-2xl space-y-4 px-4 py-6">
        <GlassCard className="p-4">
          <div className="flex items-center gap-2">
            <span className="inline-flex h-2.5 w-2.5 rounded-full bg-red-500 th-live-dot" />
            <p className="text-xs font-semibold uppercase tracking-wider text-red-300">Live Status</p>
          </div>
          <p className="mt-2 text-sm text-white/80">
            Broadcast a real video stream from your phone, or just show your crew where you&apos;re hanging out tonight.
          </p>
        </GlassCard>

        <GlassCard className="p-4 space-y-4">
          <div className="flex items-start justify-between gap-3 rounded-xl border border-white/10 bg-white/[0.03] p-3">
            <div>
              <div className="flex items-center gap-2 text-white">
                <Video className="h-4 w-4 text-red-400" />
                <span className="text-sm font-semibold">Broadcast live video</span>
              </div>
              <p className="mt-1 text-xs text-white/60">
                Share your camera + mic to your crew in real time. Turn off to just set a status badge.
              </p>
            </div>
            <Switch
              checked={videoOn}
              onCheckedChange={setVideoOn}
              data-testid="golive-video-switch"
            />
          </div>

          <div>
            <label className="text-xs uppercase tracking-wider text-white/60">Your venue — anywhere in the world</label>
            {venue ? (
              <div className="mt-2 flex items-center justify-between gap-3 rounded-xl border border-[#F6B24A]/40 bg-[#F6B24A]/10 p-3" data-testid="golive-selected-venue">
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold text-white">{venue.name}</p>
                  <p className="truncate text-xs text-white/60">{venue.city}{venue.state ? `, ${venue.state}` : ''}{typeof venue.distance_km === 'number' ? ` · ${(venue.distance_km * 0.621371).toFixed(1)} mi away` : ''}</p>
                </div>
                <button onClick={() => setVenue(null)} className="rounded-full bg-white/10 p-1.5 text-white/70 hover:bg-white/20" aria-label="Change venue" data-testid="golive-change-venue">
                  <X className="h-4 w-4" />
                </button>
              </div>
            ) : (
              <div className="mt-2 space-y-3">
                <Button
                  type="button"
                  onClick={pingLocation}
                  disabled={locating}
                  className="w-full h-11 rounded-xl bg-[#F6B24A]/15 border border-[#F6B24A]/45 text-[#F6B24A] hover:bg-[#F6B24A]/25 font-semibold"
                  variant="outline"
                  data-testid="golive-ping-location-button"
                >
                  {locating ? <Loader2 className="mr-2 h-4 w-4 th-spin" /> : <LocateFixed className="mr-2 h-4 w-4" />}
                  {locating ? 'Finding venues near you…' : 'Ping my location'}
                </Button>
                <div className="relative">
                  <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-white/40" />
                  <Input
                    value={vquery}
                    onChange={(e) => setVquery(e.target.value)}
                    placeholder="Or search any venue or city worldwide…"
                    className="h-11 rounded-xl border-white/12 bg-white/5 pl-9 text-white placeholder:text-white/40"
                    data-testid="golive-venue-search-input"
                  />
                </div>
                {(searchBusy || results.length > 0 || nearby.length > 0) && (
                  <div className="max-h-64 overflow-y-auto rounded-xl border border-white/10 bg-white/[0.03] divide-y divide-white/5" data-testid="golive-venue-options">
                    {searchBusy && <p className="p-3 text-xs text-white/50">Searching…</p>}
                    {(vquery.trim().length >= 2 ? results : nearby).map((v) => (
                      <button
                        key={v.id}
                        onClick={() => { setVenue(v); setVquery(''); setResults([]); }}
                        className="flex w-full items-center gap-2 p-3 text-left transition-colors hover:bg-white/5"
                        data-testid={`golive-venue-${v.id}`}
                      >
                        <MapPin className="h-4 w-4 shrink-0 text-[#F6B24A]" />
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-sm font-semibold text-white">{v.name}</span>
                          <span className="block truncate text-xs text-white/55">{v.city}{v.state ? `, ${v.state}` : ''}{typeof v.distance_km === 'number' ? ` · ${(v.distance_km * 0.621371).toFixed(1)} mi` : ''}</span>
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          <div>
            <label className="text-xs uppercase tracking-wider text-white/60">Duration: <span className="text-white">{hours}h</span></label>
            <Slider value={[hours]} onValueChange={(v) => setHours(v[0])} min={1} max={8} step={1} className="mt-3" data-testid="golive-hours-slider" />
          </div>

          <div>
            <label className="text-xs uppercase tracking-wider text-white/60">Add a post (optional)</label>
            <Textarea
              value={text} onChange={(e) => setText(e.target.value)}
              placeholder="Share the vibe with your friends"
              className="mt-1 bg-white/5 border-white/12 text-white placeholder:text-white/40"
              data-testid="golive-text-input"
            />
            {image ? (
              <div className="relative mt-3 overflow-hidden rounded-xl border border-white/10">
                <img src={image} className="max-h-72 w-full object-cover" alt="" />
                <button onClick={() => setImage(null)} className="absolute right-2 top-2 rounded-full bg-black/60 p-1" data-testid="golive-remove-image-button">
                  <X className="h-4 w-4 text-white" />
                </button>
              </div>
            ) : (
              <Button type="button" variant="outline" onClick={() => fileRef.current?.click()} className="mt-2 rounded-xl border-white/12 bg-white/5 text-white hover:bg-white/10" data-testid="golive-add-image-button">
                <ImagePlus className="mr-2 h-4 w-4" /> Add photo
              </Button>
            )}
            <input ref={fileRef} type="file" accept="image/*" hidden onChange={onFile} />
          </div>

          <Button onClick={submit} disabled={submitting} className="w-full h-12 rounded-xl bg-red-500 text-white hover:bg-red-600 font-semibold" data-testid="golive-submit-button">
            <Radio className="mr-2 h-4 w-4" />
            {submitting ? 'Going live…' : (videoOn ? 'Start Broadcasting' : 'Go Live')}
          </Button>
        </GlassCard>
      </main>

      <LiveBroadcasterDialog open={!!session} session={session} onStop={stopBroadcast} />
    </div>
  );
}
